import React, { useEffect, useRef, useState } from 'react'
import { CircularProgress } from '@mui/material'
import { AdCreative } from '@marketing-milk/interfaces'
import { useCreatives } from 'pages/business/creatives/useCreatives'
import { CreativeCard } from 'pages/business/creatives/CreativeCard/CreativeCard'
import { NoItemsCreated } from 'components/NoItemsCreated/NoItemsCreated'
import { isEmpty } from 'lodash'

function hasMedia(creative: AdCreative): boolean {
  if (creative.singleImage || creative.singleVideo) return true
  if (creative.carouselSlides?.some(s => s.mediaType === 'image' || s.mediaType === 'video'))
    return true
  if (creative.dynamicImages?.length || creative.dynamicVideos?.length) return true
  return false
}

function deduplicateByImage(creatives: AdCreative[]): AdCreative[] {
  const seenHashes = new Set<string>()
  return creatives.filter(creative => {
    if (!creative.singleImage) return true
    const hash = creative.singleImage.hash
    if (seenHashes.has(hash)) return false
    seenHashes.add(hash)
    return true
  })
}

const MAX_SELECTIONS = 13

type Props = {
  businessID: string
  selectedCreatives: AdCreative[]
  onToggleSelect: (creative: AdCreative) => void
  totalSelected: number
  onNext: () => void
}

export function AdSelectionGrid({
  businessID,
  selectedCreatives,
  onToggleSelect,
  totalSelected,
  onNext,
}: Props) {
  const { creatives, count, current, isFetchingNextPage, fetchNextPage } = useCreatives(
    businessID,
    { take: 50, sortProp: 'lastUsed' }
  )
  const listRef = useRef<HTMLDivElement>(null)
  const [search, setSearch] = useState('')
  const selectedIDs = selectedCreatives.map(c => c.id)
  const dedupedCreatives = deduplicateByImage((creatives ?? []).filter(hasMedia))
  const visibleCreatives = search.trim()
    ? dedupedCreatives.filter(c => c.name.toLowerCase().includes(search.toLowerCase()))
    : dedupedCreatives
  const atLimit = totalSelected >= MAX_SELECTIONS

  // Auto-fetch next pages when the content doesn't overflow the container
  // (scroll event never fires if there's nothing to scroll)
  useEffect(() => {
    const el = listRef.current
    if (!el || isFetchingNextPage || current === count) return
    if (el.scrollHeight <= el.clientHeight) {
      fetchNextPage()
    }
  }, [visibleCreatives.length, isFetchingNextPage])

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Subheader */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100 bg-gray-50 flex-shrink-0 gap-4">
        <p className="text-sm text-gray-600 flex-shrink-0">
          Pick one or more creatives to use as visual inspiration for the AI.{' '}
          <span className="text-gray-400">(max {MAX_SELECTIONS})</span>
        </p>
        <div className="flex items-center gap-3">
          <div className="relative">
            <i className="fa fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
            <input
              type="text"
              placeholder="Search by name..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent w-48"
            />
          </div>
          {creatives && (
            <span className="text-xs text-gray-400 flex-shrink-0">
              {visibleCreatives.length} unique images shown
            </span>
          )}
        </div>
      </div>

      {/* Scrollable grid */}
      <div
        className="flex-1 overflow-y-scroll px-4 pt-4"
        onScroll={e => {
          if (isFetchingNextPage || current === count) return
          const el = e.currentTarget
          if (el.scrollTop + el.clientHeight > el.scrollHeight - 100) fetchNextPage()
        }}
        ref={listRef}
      >
        <div className="grid justify-items-center w-full sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 sm:gap-5">
          {visibleCreatives.map(creative => {
            const isSelected = selectedIDs.includes(creative.id)
            const isDisabled = atLimit && !isSelected
            return (
              <div
                key={creative.id}
                className={`relative transition-opacity ${isDisabled ? 'opacity-40' : ''}`}
              >
                {/* Checkbox overlay */}
                <div
                  className={`absolute top-3 left-3 z-20 ${
                    isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
                  }`}
                  onClick={e => {
                    e.stopPropagation()
                    if (!isDisabled) onToggleSelect(creative)
                  }}
                >
                  <div
                    className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors shadow-sm ${
                      isSelected
                        ? 'bg-purple-600 border-purple-600'
                        : 'bg-white border-gray-400 hover:border-purple-400'
                    }`}
                  >
                    {isSelected && <i className="fa fa-check text-white text-xs" />}
                  </div>
                </div>

                <CreativeCard
                  creative={creative}
                  onSelect={() => {
                    if (!isDisabled) onToggleSelect(creative)
                  }}
                  pillDisplayed="format"
                  propertiesDisplayed={['name']}
                  tools={false}
                  objectFit="contain"
                  selectedText={isSelected ? 'Selected' : undefined}
                />
              </div>
            )
          })}
        </div>

        {isFetchingNextPage && (
          <div className="w-full flex justify-center py-12">
            <CircularProgress color="primary" size={48} />
          </div>
        )}

        {!isFetchingNextPage && isEmpty(visibleCreatives) && (
          <div className="w-full flex justify-center pt-20">
            <NoItemsCreated
              title={search.trim() ? 'No matches found' : 'No creatives found'}
              subtitle={
                search.trim()
                  ? `No creatives match "${search}"`
                  : 'Create some creatives first to use AI Studio'
              }
            />
          </div>
        )}

        {/* Bottom padding so cards aren't hidden behind the sticky footer */}
        <div className="h-24" />
      </div>

      {/* Sticky footer */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-white px-6 py-4 flex items-center justify-between shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
        <span className="text-sm text-gray-600">
          {totalSelected === 0
            ? 'No creatives selected'
            : `${totalSelected} / ${MAX_SELECTIONS} selected`}
          {atLimit && <span className="ml-2 text-amber-600 font-medium">Limit reached</span>}
        </span>

        <button
          type="button"
          onClick={onNext}
          disabled={selectedCreatives.length === 0}
          className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-purple-600 text-white font-semibold text-sm hover:bg-purple-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-md"
        >
          Next: Configure
          <i className="fa fa-arrow-right" />
        </button>
      </div>
    </div>
  )
}
